/**
 * Header Component
 * Styled to match R4S Macula design with user dropdown
 */

import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RefreshCw,
  Bell,
  Search,
  User,
  ChevronDown,
  Settings,
  Info,
  LogOut,
  UserCircle,
  Wifi,
  WifiOff,
  Loader2,
  AlertTriangle,
  XCircle,
  CheckCheck,
  Trash2,
} from 'lucide-react';
import clsx from 'clsx';
import { useAuth } from '../../contexts';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { useNotificationLive } from '../../hooks/useLiveUpdates';

interface HeaderProps {
  title?: string;
  subtitle?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  actions?: React.ReactNode;
}

export function Header({
  title,
  subtitle,
  searchValue,
  onSearchChange,
  onRefresh,
  isRefreshing,
  actions,
}: HeaderProps) {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { connectionState, reconnect, lastError } = useWebSocket();
  const { notifications, unreadCount, markAllRead, clearAll } = useNotificationLive();

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(event.target as Node)) {
        setIsNotifOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    setIsUserMenuOpen(false);
    logout();
    navigate('/login');
  };

  // Get initials for avatar
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <header className="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-6 shadow-sm">
      {/* Title */}
      <div className="flex items-center gap-4">
        {title && (
          <h1 className="text-lg font-semibold text-gray-800">{title}</h1>
        )}
        {subtitle && (
          <span className="text-sm text-gray-500">{subtitle}</span>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3">
        {/* Search */}
        {onSearchChange && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={searchValue || ''}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-56 pl-9 pr-4 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#45bdd6] focus:border-transparent bg-gray-50"
            />
          </div>
        )}

        {/* Custom Actions */}
        {actions}

        {/* Refresh Button */}
        {onRefresh && (
          <button
            onClick={onRefresh}
            disabled={isRefreshing}
            className={clsx(
              'p-2 text-gray-500 hover:text-[#45bdd6] hover:bg-gray-100 rounded-lg transition-colors',
              isRefreshing && 'animate-spin'
            )}
            title="Refresh"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        )}

        {/* WebSocket Connection Status */}
        <button
          onClick={connectionState !== 'connected' ? reconnect : undefined}
          className={clsx(
            'p-2 rounded-lg transition-colors relative group',
            connectionState === 'connected' && 'text-emerald-500 hover:bg-emerald-50',
            connectionState === 'connecting' && 'text-amber-500 hover:bg-amber-50',
            connectionState === 'reconnecting' && 'text-amber-500 hover:bg-amber-50',
            connectionState === 'disconnected' && 'text-gray-400 hover:text-red-500 hover:bg-red-50'
          )}
          title={
            connectionState === 'connected'
              ? 'Live updates active'
              : connectionState === 'reconnecting'
                ? lastError || 'Reconnecting...'
                : connectionState === 'connecting'
                  ? 'Connecting...'
                  : 'Disconnected — click to reconnect'
          }
        >
          {connectionState === 'connected' && (
            <Wifi className="w-4 h-4" />
          )}
          {connectionState === 'disconnected' && (
            <WifiOff className="w-4 h-4" />
          )}
          {(connectionState === 'connecting' || connectionState === 'reconnecting') && (
            <Loader2 className="w-4 h-4 animate-spin" />
          )}
          {connectionState === 'connected' && (
            <span className="absolute top-1 right-1 w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
          )}
        </button>

        {/* Notifications */}
        <div className="relative" ref={notifRef}>
          <button
            onClick={() => { setIsNotifOpen((prev) => !prev); if (!isNotifOpen) markAllRead(); }}
            className="p-2 text-gray-500 hover:text-[#45bdd6] hover:bg-gray-100 rounded-lg transition-colors relative"
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>

          {/* Notification Dropdown */}
          {isNotifOpen && (
            <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden">
              {/* Header */}
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-900">Notifications</span>
                <div className="flex items-center gap-2">
                  {notifications.length > 0 && (
                    <>
                      <button
                        onClick={() => { markAllRead(); }}
                        className="text-xs text-[#45bdd6] hover:underline flex items-center gap-1"
                        title="Mark all read"
                      >
                        <CheckCheck className="w-3 h-3" /> Read
                      </button>
                      <button
                        onClick={() => { clearAll(); setIsNotifOpen(false); }}
                        className="text-xs text-gray-400 hover:text-red-500 flex items-center gap-1"
                        title="Clear all"
                      >
                        <Trash2 className="w-3 h-3" /> Clear
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Notification List */}
              <div className="max-h-80 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="px-4 py-8 text-center text-sm text-gray-400">
                    <Bell className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    No notifications
                  </div>
                ) : (
                  notifications.slice(0, 20).map((n, i) => (
                    <div
                      key={`${n.timestamp}-${i}`}
                      className="px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-start gap-2.5">
                        <div className={clsx(
                          'mt-0.5 p-1 rounded-full flex-shrink-0',
                          n.level === 'error' && 'bg-red-100 text-red-500',
                          n.level === 'warning' && 'bg-amber-100 text-amber-500',
                          n.level === 'info' && 'bg-blue-100 text-blue-500',
                        )}>
                          {n.level === 'error' && <XCircle className="w-3 h-3" />}
                          {n.level === 'warning' && <AlertTriangle className="w-3 h-3" />}
                          {n.level === 'info' && <Info className="w-3 h-3" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{n.title}</p>
                          <p className="text-xs text-gray-500 line-clamp-2">{n.message}</p>
                          <p className="text-[10px] text-gray-400 mt-1">
                            {new Date(n.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* User Menu */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            className="flex items-center gap-2 pl-3 border-l border-gray-200 hover:bg-gray-50 rounded-lg py-1 pr-2 transition-colors"
          >
            <div className="w-8 h-8 bg-gradient-to-br from-[#45bdd6] to-[#674EA7] rounded-full flex items-center justify-center">
              {user ? (
                <span className="text-white text-xs font-bold">
                  {getInitials(user.username)}
                </span>
              ) : (
                <User className="w-4 h-4 text-white" />
              )}
            </div>
            <div className="text-left hidden sm:block">
              <span className="text-sm font-medium text-gray-700">
                {user?.username || 'Guest'}
              </span>
            </div>
            <ChevronDown className={clsx(
              "w-4 h-4 text-gray-500 transition-transform",
              isUserMenuOpen && "rotate-180"
            )} />
          </button>

          {/* Dropdown Menu */}
          {isUserMenuOpen && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
              {/* User Info */}
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-900">
                    {user?.username || 'Guest'}
                  </p>
                  {user && (
                    <span className={clsx(
                      "px-2 py-0.5 text-xs font-medium rounded-full",
                      user.is_admin
                        ? "bg-purple-100 text-purple-700"
                        : "bg-gray-100 text-gray-600"
                    )}>
                      {user.is_admin ? 'Admin' : 'Viewer'}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500">
                  {user?.email || ''}
                </p>
              </div>

              {/* Menu Items */}
              <div className="py-1">
                <button
                  onClick={() => {
                    navigate('/settings');
                    setIsUserMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <UserCircle className="w-4 h-4 text-gray-400" />
                  Profile
                </button>
                <button
                  onClick={() => {
                    navigate('/settings');
                    setIsUserMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Settings className="w-4 h-4 text-gray-400" />
                  Settings
                </button>
                <button
                  onClick={() => {
                    navigate('/info');
                    setIsUserMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Info className="w-4 h-4 text-gray-400" />
                  Info
                </button>
              </div>

              {/* Logout */}
              <div className="border-t border-gray-100 py-1">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
